import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { differenceInMinutes, parse } from 'date-fns';
import { skipToken } from '@reduxjs/toolkit/query';
import GeoMapWithTiles from '../../../features/geo-map/GeoMapWithTiles';
import style from './StatePanel.module.css';
import RouteVectorLayer from '../../../shared/ui/GeoMap/RouteVectorLayer';
import ControlledTable from '../../../shared/ui/ControlledTable/ControlledTable';
import AlertsResourceBll from '../../../entities/alerts/alertsResource/AlertsResourceBll/AlertsResourceBll';
import { alertsResourceType } from '../../../entities/alerts/alertsResource/redux/alertsResource.slice';
import TrackPointsResourceBll from '../../../entities/vehicles/trackPointsResource/TrackPointsResourceBll/TrackPointsResourceBll';
import { platoonGroupsResourceType } from '../../../entities/vehicles/trackPointsResource/redux/trackPointsResource.slice';
// eslint-disable-next-line boundaries/element-types
import MarkerVectorLayer from '../../../shared/ui/GeoMap/MarkerVectorLayer';
import { vehicleType } from '../../../entities/vehicles/vehicle/redux/vehicle.slice';
import convertFormatDate from '../../../shared/utils/convertFormatDate';
import isCurrentDateInRange from '../../../shared/utils/isCurrentDateInRange';
import calculateSpeed from '../../../shared/utils/calculateSpeed';
import splitRoutes from '../../../shared/utils/splitRoutes';
import { followModeActions, FollowModeEnum } from '../../../entities/map/followMode/redux/followMode.slice';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import getCurrentTimeByOffset from '../../../shared/utils/getCurrentTimeByTimezone';
import InfoText from './styles/InfoText';
import useTimeZone from '../../../entities/timeZone/hooks/useTimeZone';
import reducerPath from '../../../app/reducerPath';
import mapNames from '../../../entities/map/constants';
import SensorsState from './SensorsState/SensorsState';
import useAuth from '../../../shared/lib/hooks/useAuth';
import { useGetAppUserResourceIdQuery } from '../../../entities/account/appUsersResource/redux/appUsersResource.api';
import { useGetVehicleSensorsLiveQuery } from '../../../entities/equipments/sensorLiveValues/sensorLiveValues';
import transformCoords from '../../../shared/utils/transformCoords';

const BLACKBOX_SENSOR_TYPE_ID = 14;

const alertsHeadCells = [
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Сообщение',
  },
  {
    id: 'beginTime',
    numeric: false,
    disablePadding: false,
    label: 'Отправлено',
  },
  {
    id: 'severity',
    numeric: false,
    disablePadding: false,
    label: 'Серьезность',
  },
  {
    id: 'coordinatesWithIco',
    numeric: false,
    disablePadding: false,
    label: 'Координаты',
  },
];

const alertsDisplayedCells = ['description', 'beginTime', 'severity', 'coordinatesWithIco'];

const problems = {
  top: 'top',
  left: 'left',
  right: 'right',
  bottom: 'bottom',
};

function StatePanel({ data, vehicleId }) {
  const vehicle = data?.dataVehicleIdResource?.data;
  const [alerts, setAlerts] = useState(null);
  // eslint-disable-next-line no-shadow
  const setAlertsProps = useCallback((data) => setAlerts(data), []);
  const [activeCoordinates, setActiveCoordinates] = useState(null);
  const [activeRoute, setActiveRoute] = useState(null);
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(followModeActions.changeMode({ [FollowModeEnum.isActive]: true }));
  }, []);
  useEffect(() => {
    setActiveCoordinates(null);
  }, [vehicleId]);
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          marginTop: '40px',
          position: 'relative',
        }}
      >
        <TrackPointsResourceBll vehicleId={vehicleId}>
          <HazardInformation
            activeCoordinates={activeCoordinates}
            alerts={alerts}
            vehicle={vehicle}
            activeRoute={activeRoute}
          />
        </TrackPointsResourceBll>
        {/* <TrafficLight /> */}
      </Box>
      <AlertsResourceBll vehicleId={vehicleId} setActiveRoute={setActiveRoute}>
        <Alerts setActiveCoordinates={setActiveCoordinates} setAlertsProps={setAlertsProps} />
      </AlertsResourceBll>
    </Box>
  );
}

StatePanel.propTypes = {
  vehicleId: PropTypes.number,
  data: PropTypes.shape({
    dataVehicleIdResource: PropTypes.shape({
      data: PropTypes.shape(vehicleType),
    }),
  }),
};

StatePanel.defaultProps = {
  vehicleId: null,
  data: null,
};

function Alerts({ data, setAlertsProps, setActiveCoordinates }) {
  const alertsArr = data?.dataAlertsResource?.data;

  useEffect(() => {
    if (alertsArr) {
      setAlertsProps(alertsArr);
    }
  }, [data]);

  return (
    <Box sx={{ marginTop: '50px' }}>
      <Typography
        sx={{
          fontWeight: '400',
          fontSize: '20px',
          lineHeight: '24px',
          marginBottom: '10px',
        }}
      >
        Последние сообщения
      </Typography>
      <Box>
        <ControlledTable
          disableCheckbox
          isSelectOnClickRow={false}
          rows={alertsArr?.map((e, i) => {
            const newData = JSON.parse(JSON.stringify(e));
            newData.id = i;
            newData.beginTime = convertFormatDate(e.beginTime).toFormat;
            return newData;
          })}
          headCells={alertsHeadCells}
          displayedCells={alertsDisplayedCells}
          countRowsPerPage={5}
          selected={[]}
          setSelected={(e) => {
            setActiveCoordinates({
              description: alertsArr[e[0]]?.description,
              latitude: alertsArr[e[0]]?.latitude,
              longitude: alertsArr[e[0]]?.longitude,
            });
          }}
        />
      </Box>
    </Box>
  );
}

Alerts.propTypes = {
  data: PropTypes.shape({
    dataAlertsResource: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape(alertsResourceType)),
    }),
  }),
  setAlertsProps: PropTypes.func,
  setActiveCoordinates: PropTypes.func,
};

Alerts.defaultProps = {
  data: null,
  setAlertsProps: null,
  setActiveCoordinates: null,
};

function divideByTenMillion(number) {
  return number / 10000000;
}

function calculateDirection({ clonProps, clatProps, plonProps, platProps }) {
  const clon = clonProps ? divideByTenMillion(clonProps) : null;
  const clat = clatProps ? divideByTenMillion(clatProps) : null;
  const plon = plonProps ? divideByTenMillion(plonProps) : null;
  const plat = platProps ? divideByTenMillion(platProps) : null;
  if (plat && plon && clat && clon) {
    // Вектор текущего движения
    const vlon = clon - plon;
    const vlat = clat - plat;
    const length = Math.sqrt(vlat * vlat + vlon * vlon);
    const sinA = vlon / length;
    const a = Math.asin(sinA);
    const signCosA = Math.sign(vlat / length);
    let degree = (180 / Math.PI) * a;

    if (signCosA === -1) {
      degree = 180 - degree;
    }
    if (degree < 0) {
      degree += 360;
    }
    return Math.round(degree * 100) / 100;
  }
  return undefined;
}

function calculateDenmSide({ clonProps, clatProps, plonProps, platProps, dlatProps, dlonProps }) {
  const clon = clonProps ? divideByTenMillion(clonProps) : null;
  const clat = clatProps ? divideByTenMillion(clatProps) : null;
  const plon = plonProps ? divideByTenMillion(plonProps) : null;
  const plat = platProps ? divideByTenMillion(platProps) : null;
  const dlat = dlatProps ? divideByTenMillion(dlatProps) : null;
  const dlon = dlonProps ? divideByTenMillion(dlonProps) : null;

  if (plat && plon && clat && clon && dlat && dlon) {
    // Вектор текущего движения
    const vlon = clon - plon;
    const vlat = clat - plat;
    const newDlat = dlat;
    const newDlon = dlon;
    // Вектор направления на опасность
    const vDClat = newDlat - clat;
    const vDClon = newDlon - clon;
    const sinA =
      (vDClat * vlon + vDClon * vlat) /
      Math.sqrt(vDClat * vDClat + vDClon * vDClon) /
      Math.sqrt(vlat * vlat + vlon * vlon);
    const signCosA = Math.sign(vDClat * vlat + vDClon * vlon);
    const a = Math.asin(sinA);

    let degrees = (180 / Math.PI) * a;
    if (signCosA === -1) {
      degrees = 180 - degrees;
    }

    if (degrees > 180) {
      degrees -= 360;
    }

    const res = [];

    if (degrees >= -45 && degrees <= 45) {
      res.push(problems.top);
    } else if (degrees > 45 && degrees <= 135) {
      res.push(problems.left);
    } else if (degrees > -135 && degrees <= -45) {
      res.push(problems.right);
    } else {
      res.push(problems.bottom);
    }

    return { denmSide: res, direction: degrees };
  }
  return { denmSide: [], direction: 0 };
}

function HazardInformation({ data, vehicle, alerts, activeCoordinates, activeRoute }) {
  const { user } = useAuth();
  const { data: dataAppUserResourceId } = useGetAppUserResourceIdQuery({
    id: user.appUserId,
  });
  const appUser = dataAppUserResourceId?.data;

  const trackPoints = useMemo(() => data?.dataTrackPointsResource?.data, [data?.dataTrackPointsResource]);
  const [vehicleProblem, setVehicleProblem] = useState([]);
  const [direction, setDirection] = useState(null);
  const [speed, setSpeed] = useState(null);

  const [date, setDate] = useState(null);
  const [isExpiredDate, setIsExpiredDate] = useState(false);

  const mapName = mapNames.vehicleState;

  const isFullscreen = useAppSelector((state) => state[`${reducerPath.fullScreenMap}`])[mapName];

  const savedView = useAppSelector((state) => state[`${reducerPath.positionMap}`][mapName]);

  const [extent, setExtent] = useState(savedView?.extent);

  const sensorIds = useMemo(
    () => [
      BLACKBOX_SENSOR_TYPE_ID,
      ...(appUser?.customAugmentationEx?.view?.vsensor?.split(',')?.map(Number).filter(Boolean) || []),
    ],
    [appUser],
  );

  const { vehicleId } = vehicle || {};
  const shouldSubscribe = vehicleId && sensorIds.length > 0;
  const { data: socketData } = useGetVehicleSensorsLiveQuery(
    shouldSubscribe ? { vehicleIds: [vehicleId], sensorIds } : skipToken,
  );
  const liveSensorValues = socketData?.data ?? [];

  const blackboxValue = liveSensorValues.find((s) => s.sensorId === BLACKBOX_SENSOR_TYPE_ID)?.value;

  useEffect(() => {
    setExtent(savedView?.extent);
  }, [isFullscreen]);

  const utc = useTimeZone();

  useEffect(() => {
    if (!trackPoints?.length) {
      setDate(null);
      setIsExpiredDate(false);
      return;
    }

    const lastUpdate = trackPoints?.[0]?.date;
    const now = new Date();

    setIsExpiredDate(differenceInMinutes(now, new Date(lastUpdate)) >= 5);
    setDate(convertFormatDate(lastUpdate, utc).toFormat);
  }, [trackPoints, utc]);

  // проверка актуальности даты обновления данных
  useEffect(() => {
    const timer = setInterval(() => {
      if (!date) return;

      const now = getCurrentTimeByOffset(utc);
      const lastUpdate = parse(date, 'dd.MM.yyyy HH:mm:ss', new Date());

      setIsExpiredDate(differenceInMinutes(now, lastUpdate) >= 5);
    }, 60 * 1000); // проверяем каждую минуту

    return () => clearInterval(timer);
  }, [date, utc]);

  useEffect(() => {
    setVehicleProblem(() => []);
    if (trackPoints?.length >= 2 && alerts?.length) {
      const coordinatesNow = trackPoints[0];
      const coordinatesPrev = trackPoints[1];

      alerts?.forEach((alertsNow) => {
        if (alertsNow.alertType?.alertTypeId === 100 || alertsNow.alertType?.alertTypeId === 101) {
          // новые вычисления
          if (isCurrentDateInRange(alertsNow.beginTime, alertsNow.endTime)) {
            // координаты опасности (из активного сообщения)
            const { dLat, dLon } = { dLat: alertsNow.latitude, dLon: alertsNow.longitude };
            if (dLat && dLon) {
              // текущие координаты ТС;
              const { cLat, cLon } = { cLat: coordinatesNow.latitude, cLon: coordinatesNow.longitude };
              // предыдущие координаты ТС
              const { pLat, pLon } = { pLat: coordinatesPrev.latitude, pLon: coordinatesPrev.longitude };

              const { denmSide } = calculateDenmSide({
                clonProps: cLon,
                clatProps: cLat,
                plonProps: pLon,
                platProps: pLat,
                dlatProps: dLat,
                dlonProps: dLon,
              });
              setVehicleProblem(denmSide);
            }
          }
        }
      });
    }
  }, [trackPoints, alerts]);

  const [trackPointsMain, setTrackPointsMain] = useState([]);

  useEffect(() => {
    setVehicleProblem(() => []);
    if (trackPoints?.length >= 2) {
      const coordinatesNow = trackPoints[0];
      const coordinatesPrev = trackPoints[1];

      const startLatitude = coordinatesPrev.latitude;
      const startLongitude = coordinatesPrev.longitude;
      const startDate = coordinatesPrev.date;
      const finishLatitude = coordinatesNow.latitude;
      const finishLongitude = coordinatesNow.longitude;
      const finishDate = coordinatesNow.date;

      setSpeed(
        Math.round(
          calculateSpeed(startLatitude, startLongitude, startDate, finishLatitude, finishLongitude, finishDate),
        ),
      );

      const directionVal = calculateDirection({
        clonProps: finishLongitude,
        clatProps: finishLatitude,
        plonProps: startLongitude,
        platProps: startLatitude,
      });
      setDirection(directionVal);
    }
    setTrackPointsMain(trackPoints?.length ? JSON.parse(JSON.stringify(trackPoints)) : []);
  }, [trackPoints]);

  return (
    <>
      <Box sx={{ width: '360px', height: '400px' }}>
        <Box className={isFullscreen ? style.geoMapWithTilesMax : style.geoMapWithTiles}>
          <GeoMapWithTiles styles={isFullscreen ? style.geoMapMax : style.geoMap} extent={extent} name={mapName}>
            <RouteVectorLayer
              name={mapName}
              coordinatesWithDate={
                trackPointsMain?.length &&
                splitRoutes(trackPointsMain).map((currentCoordinates) =>
                  currentCoordinates.map((point) => ({
                    coordinates: transformCoords(point.longitude, point.latitude),
                    date: point.date,
                  })),
                )
              }
              vehicleId={trackPoints?.[0]?.vehicleId}
              isManyRoutesMode
              isFollowReverseMode
            />
            <MarkerVectorLayer
              text={`${vehicle?.num}${activeRoute ? `(${activeRoute})` : ''}`}
              vehicleModelId={vehicle?.model?.vehicleModelId}
              vehicleIconColor={`#${vehicle?.customAugmentationEx?.view?.['icon-color']}`}
              coordinates={trackPoints?.map((e) => transformCoords(e.longitude, e.latitude))[0] || []}
            />

            {activeCoordinates && (
              <MarkerVectorLayer
                text={activeCoordinates.description}
                vehicleModelId={vehicle?.model?.vehicleModelId}
                vehicleIconColor={`#${vehicle?.customAugmentationEx?.view?.['icon-color']}`}
                coordinates={[...transformCoords(activeCoordinates.longitude, activeCoordinates.latitude)]}
              />
            )}
          </GeoMapWithTiles>
        </Box>
      </Box>
      <InfoVehicle
        vehicleProblem={vehicleProblem}
        vehicle={vehicle}
        direction={direction}
        date={date}
        speed={speed}
        isExpiredDate={isExpiredDate}
        blackboxValue={blackboxValue}
      />
      <SensorsState liveSensorValues={liveSensorValues} vehicleId={vehicle?.vehicleId}/>
    </>
  );
}

HazardInformation.propTypes = {
  activeRoute: PropTypes.string,
  data: PropTypes.shape({
    dataTrackPointsResource: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape(platoonGroupsResourceType)),
    }),
  }),
  alerts: PropTypes.arrayOf(PropTypes.shape(alertsResourceType)),
  vehicle: PropTypes.shape(vehicleType),
  activeCoordinates: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    description: PropTypes.string,
  }),
};

HazardInformation.defaultProps = {
  activeRoute: null,
  data: null,
  alerts: null,
  vehicle: null,
  activeCoordinates: null,
};

function InfoVehicle({ vehicleProblem, vehicle, date, direction, speed, isExpiredDate, blackboxValue }) {
  return (
    <Box sx={{ display: 'flex', gap: '24px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ position: 'relative', marginLeft: '30px', marginRight: '30px' }}>
          {vehicleProblem.includes(problems.top) && (
            <img
              style={{
                position: 'absolute',
                top: '-28px',
                left: '50%',
                transform: 'translate(-50%, 0)',
              }}
              src="/img/problemY.png"
              alt="ошибка по оси Y"
            />
          )}
          {vehicleProblem.includes(problems.bottom) && (
            <img
              style={{
                position: 'absolute',
                bottom: '-28px',
                left: '50%',
                transform: 'translate(-50%, 0) rotate(180deg)',
              }}
              src="/img/problemY.png"
              alt="ошибка по оси Y"
            />
          )}
          {vehicleProblem.includes(problems.left) && (
            <img
              style={{
                position: 'absolute',
                left: '-30px',
                top: 0,
              }}
              src="/img/problemX.png"
              alt="ошибка по оси X"
            />
          )}
          {vehicleProblem.includes(problems.right) && (
            <img
              style={{
                position: 'absolute',
                right: '-30px',
                top: 0,
                transform: 'rotate(180deg)',
              }}
              src="/img/problemX.png"
              alt="ошибка по оси Y"
            />
          )}

          <img src="/img/carBlue.png" alt="авто" />
        </Box>
      </Box>
      <Box sx={{ maxWidth: 'max-content', minWidth: '200px' }}>
        <InfoText isError={isExpiredDate}>
          Обновление: {date} {!!blackboxValue && blackboxValue > 1 ? `(${blackboxValue})` : ''}
        </InfoText>
        <InfoText>Скорость: {speed === Infinity ? 0 : speed || 0} км/ч</InfoText>
        <InfoText>Топливо: данных нет</InfoText>
        <InfoText>
          Направление движения:
          {direction ? ` ${direction}°` : ' нет данных'}
        </InfoText>
        <InfoText sx={{ marginTop: '20px' }}>
          ГосНомер:
          {vehicle?.num}
        </InfoText>
        <InfoText>Пробег: данных нет</InfoText>
        <InfoText sx={{ marginTop: '20px' }}>Комментарий:</InfoText>
        <InfoText>{vehicle?.model.name}</InfoText>
      </Box>
    </Box>
  );
}

InfoVehicle.propTypes = {
  vehicleProblem: PropTypes.arrayOf(PropTypes.string),
  vehicle: PropTypes.shape(vehicleType),
  date: PropTypes.string,
  direction: PropTypes.number,
  speed: PropTypes.number,
  isExpiredDate: PropTypes.bool,
  blackboxValue: PropTypes.number,
};

InfoVehicle.defaultProps = {
  vehicle: null,
  vehicleProblem: null,
  date: null,
  direction: null,
  speed: null,
  isExpiredDate: false,
  blackboxValue: null,
};

export default StatePanel;
